'use client';

import React, { useState, useEffect } from 'react';
import { Phone, MessageCircle, Mail, User, Shield, CheckCircle, Loader2, Bell } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import { Property } from '@/lib/mockProperties';
import { inquiryService } from '@/lib/services/dataService';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

interface ContactOwnerModalProps {
  property: Property;
  onClose: () => void;
}

interface UnlockedContactDetails {
  ownerName: string;
  ownerPhone: string;
  ownerEmail: string;
  address: string;
  googleMapsLink: string;
  landmarks: string;
}

const CLICK_THRESHOLD = 5;

export default function ContactOwnerModal({ property, onClose }: ContactOwnerModalProps) {
  const [inquirySent, setInquirySent] = useState(false);
  const [sendingInquiry, setSendingInquiry] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [ownerResponse, setOwnerResponse] = useState<string | null>(null);
  const [showOwnerPrompt, setShowOwnerPrompt] = useState(false);
  const [savingResponse, setSavingResponse] = useState(false);
  const [contactDetails, setContactDetails] = useState<UnlockedContactDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const { user } = useAuth();

  const storageKey = `contact_clicks_${property.id}`;

  // Fetch sensitive contact details from server on mount
  useEffect(() => {
    const fetchDetails = async () => {
      setLoadingDetails(true);
      try {
        const res = await fetch(`/api/property/contact-details?listing_id=${property.id}`);
        if (res.ok) {
          const data: UnlockedContactDetails = await res.json();
          setContactDetails(data);
        }
      } catch {
        // silent
      } finally {
        setLoadingDetails(false);
      }
    };
    fetchDetails();
  }, [property.id]);

  useEffect(() => {
    // Load existing click count from localStorage
    const stored = localStorage.getItem(storageKey);
    const count = stored ? parseInt(stored, 10) : 0;
    setClickCount(count);
    if (count >= CLICK_THRESHOLD) {
      setShowOwnerPrompt(true);
    }
    // Load existing owner response from DB
    const loadResponse = async () => {
      try {
        const supabase = createClient();
        const { data } = await supabase
          .from('listings')
          .select('owner_inquiry_response')
          .eq('id', property.id)
          .single();
        if (data?.owner_inquiry_response) {
          setOwnerResponse(data.owner_inquiry_response);
        }
      } catch { /* non-critical */ }
    };
    loadResponse();
  }, [property.id, storageKey]);

  const whatsappMsg = contactDetails
    ? encodeURIComponent(`Hi, I'm interested in your property: ${property.title} listed on tolettu.com. Is it still available?`)
    : '';

  const handleContactClick = async () => {
    if (inquirySent || sendingInquiry) return;
    setSendingInquiry(true);

    // Increment click count
    const newCount = clickCount + 1;
    setClickCount(newCount);
    localStorage.setItem(storageKey, String(newCount));

    if (newCount >= CLICK_THRESHOLD) {
      setShowOwnerPrompt(true);
    }

    try {
      const success = await inquiryService.createInquiry({
        listing_id: property.id,
        tenant_id: user?.id || null,
        tenant_name: user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Anonymous',
        tenant_email: user?.email || '',
        tenant_phone: user?.user_metadata?.phone || '',
        message: `Interested in: ${property.title}`,
      });

      if (!success) {
        const supabase = createClient();
        await supabase.rpc('increment_listing_inquiries', { listing_id: property.id }).maybeSingle();
      }

      const supabase = createClient();
      await supabase.rpc('increment_listing_contact_clicks', { listing_id: property.id }).maybeSingle();

      setInquirySent(true);
    } catch {
      // non-critical — still allow contact
    } finally {
      setSendingInquiry(false);
    }
  };

  const handleOwnerResponse = async (response: 'Yes' | 'No') => {
    setSavingResponse(true);
    try {
      const supabase = createClient();
      await supabase
        .from('listings')
        .update({ owner_inquiry_response: response })
        .eq('id', property.id);
      setOwnerResponse(response);
    } catch { /* non-critical */ } finally {
      setSavingResponse(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Contact Property Owner" size="sm">
      <div className="p-6 space-y-5">
        {/* Owner Info */}
        <div className="flex items-center gap-3 p-4 bg-muted rounded-xl">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <User size={22} className="text-primary" />
          </div>
          <div>
            <div className="font-bold text-foreground text-sm">{property.ownerName}</div>
            <div className="text-xs text-muted-foreground">Property Owner</div>
            {property.verified && (
              <div className="flex items-center gap-1 text-xs text-blue-600 font-medium mt-0.5">
                <Shield size={10} />
                ID Verified
              </div>
            )}
          </div>
        </div>

        {/* Property Summary */}
        <div className="p-3 bg-secondary/40 rounded-xl">
          <div className="text-xs font-semibold text-muted-foreground mb-1">Property</div>
          <div className="text-sm font-bold text-foreground">{property.title}</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {property.locality}, {property.city} · ₹{property.monthlyRent.toLocaleString('en-IN')}/month
          </div>
        </div>

        {/* Owner Inquiry Prompt — shown after 5 clicks */}
        {showOwnerPrompt && (
          <div className="p-4 rounded-xl border border-amber-200 bg-amber-50">
            <div className="flex items-center gap-2 mb-2">
              <Bell size={15} className="text-amber-600 shrink-0" />
              <span className="text-sm font-bold text-amber-800">Message for Property Owner</span>
            </div>
            <p className="text-sm text-amber-700 mb-3">
              Does anybody take in your house?
            </p>
            {ownerResponse ? (
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold ${
                ownerResponse === 'Yes' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                <CheckCircle size={14} />
                Owner responded: <span className="font-bold">{ownerResponse}</span>
                {ownerResponse === 'Yes' && ' — This property may be taken'}
                {ownerResponse === 'No' && ' — Still available!'}
              </div>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => handleOwnerResponse('Yes')}
                  disabled={savingResponse}
                  className="flex-1 py-2 rounded-xl bg-green-600 text-white text-sm font-bold hover:bg-green-700 transition-colors disabled:opacity-60"
                >
                  {savingResponse ? <Loader2 size={14} className="animate-spin mx-auto" /> : 'Yes'}
                </button>
                <button
                  onClick={() => handleOwnerResponse('No')}
                  disabled={savingResponse}
                  className="flex-1 py-2 rounded-xl bg-red-500 text-white text-sm font-bold hover:bg-red-600 transition-colors disabled:opacity-60"
                >
                  {savingResponse ? <Loader2 size={14} className="animate-spin mx-auto" /> : 'No'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Inquiry saved indicator */}
        {inquirySent && (
          <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-xl px-3 py-2 text-xs font-semibold">
            <CheckCircle size={13} />
            Inquiry recorded — owner will be notified
          </div>
        )}

        {/* Contact Options */}
        {loadingDetails ? (
          <div className="flex items-center justify-center gap-2 py-6 text-xs text-muted-foreground">
            <Loader2 size={16} className="animate-spin" />
            Loading contact details...
          </div>
        ) : contactDetails ? (
          <div className="space-y-2.5">
            <a
              href={`tel:${contactDetails.ownerPhone}`}
              onClick={handleContactClick}
              className="flex items-center gap-3 p-3.5 rounded-xl border border-border hover:border-primary hover:bg-secondary/30 transition-all duration-150 group"
            >
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                <Phone size={16} className="text-primary group-hover:text-white" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-bold text-foreground">Call Owner</div>
                <div className="text-xs text-muted-foreground">{contactDetails.ownerPhone}</div>
              </div>
              {sendingInquiry && <Loader2 size={14} className="animate-spin text-muted-foreground" />}
            </a>

            <a
              href={`https://wa.me/91${contactDetails.ownerPhone.replace(/\D/g, '')}?text=${whatsappMsg}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleContactClick}
              className="flex items-center gap-3 p-3.5 rounded-xl border border-border hover:border-green-500 hover:bg-green-50 transition-all duration-150 group"
            >
              <div className="w-9 h-9 rounded-xl bg-green-50 flex items-center justify-center group-hover:bg-green-600 transition-colors">
                <MessageCircle size={16} className="text-green-600 group-hover:text-white" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-bold text-foreground">WhatsApp</div>
                <div className="text-xs text-muted-foreground">Chat instantly on WhatsApp</div>
              </div>
            </a>

            <a
              href={`mailto:${contactDetails.ownerEmail}?subject=Inquiry about ${property.title}&body=Hi, I'm interested in your property listed on Tolettu.com.`}
              onClick={handleContactClick}
              className="flex items-center gap-3 p-3.5 rounded-xl border border-border hover:border-accent hover:bg-amber-50 transition-all duration-150 group"
            >
              <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center group-hover:bg-accent transition-colors">
                <Mail size={16} className="text-accent group-hover:text-white" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-bold text-foreground">Send Email</div>
                <div className="text-xs text-muted-foreground">{contactDetails.ownerEmail}</div>
              </div>
            </a>
          </div>
        ) : (
          <div className="text-xs text-center text-muted-foreground py-4">
            Could not load contact details. Please try again.
          </div>
        )}

        {/* Safety Note */}
        <p className="text-xs text-muted-foreground text-center leading-relaxed">
          🔒 tolettu.com verifies owners before listing. Never pay advance rent without visiting the property.
        </p>
      </div>
    </Modal>
  );
}